import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import logoWhite from '../assets/logo-white.png';
import logoDark from '../assets/logo-dark.png';
import ThemeToggle from './ThemeToggle';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useBreadcrumb } from '../contexts/BreadcrumbContext';
import { subscribeToCustomersPendingCount, subscribeToTerraFlowProjectsPendingCount, subscribeToTerraFlowProjectsChangeCount } from '../utils/storage';
import { NotificationBadge } from './templates';
import { APP_VERSION } from '../version';
import './Layout.css';
import './RolesPage.css';   // reuse .roles-back-btn styling for the admin-only Back to Portal button

// `variant` selects which application shell this Layout renders:
//   'tirp'       → Reporting Portal (full MENU + DB MANAGEMENT, bridges)
//   'terraflow'  → TerraFlow Manager (slim MENU, bridges)
//   'mergepoint' → Mergepoint (DOCX pack re-assembly, bridges)
// All share the topbar/profile/sign-out chrome; only the title line, the
// logo-click home target, and the sidebar sections differ.
export default function Layout({ variant = 'tirp' }) {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { logout, userProfile } = useAuth();
  const { crumbs } = useBreadcrumb();
  const logo = theme === 'dark' ? logoWhite : logoDark;
  // Admin-only "Back to Portal" shortcut, shown on every main-app-shell page
  // (TIRP / TerraFlow / Mergepoint). The standalone analysis tabs use their
  // own layout with a Save-and-close button, so they never show this.
  const isAdmin = !!userProfile?.roles?.includes('admin');
  const showPortalBack = isAdmin;

  const isTerraFlow = variant === 'terraflow';
  const isMergepoint = variant === 'mergepoint';
  const homePath = isTerraFlow ? '/terraflow' : isMergepoint ? '/mergepoint' : '/dashboard';
  const titleLine2 = isTerraFlow ? 'TerraFlow Manager' : isMergepoint ? 'Mergepoint' : 'Reporting Portal';

  // Pending-assignment badges (TerraFlow shell only): unassigned customers on
  // the Customers nav, and projects with no linked TIB object on Dashboard.
  const [pendingCustomers, setPendingCustomers] = useState(0);
  const [pendingProjects, setPendingProjects] = useState(0);
  const [changedProjects, setChangedProjects] = useState(0);
  useEffect(() => {
    if (!isTerraFlow) return undefined;
    return subscribeToCustomersPendingCount(setPendingCustomers);
  }, [isTerraFlow]);
  useEffect(() => {
    if (!isTerraFlow) return undefined;
    return subscribeToTerraFlowProjectsPendingCount(setPendingProjects);
  }, [isTerraFlow]);
  useEffect(() => {
    if (!isTerraFlow) return undefined;
    return subscribeToTerraFlowProjectsChangeCount(setChangedProjects);
  }, [isTerraFlow]);

  return (
    <div className="layout">
      {/* ── Top Bar ── */}
      <header className="topbar">
        <div className="topbar-left" onClick={() => navigate(homePath)} role="button" tabIndex={0} style={{ cursor: 'pointer' }}>
          <img src={logo} alt="Terra Inspectioneering" className="topbar-logo" />
          <div className="topbar-title">
            <span className="topbar-title-line1">Terra Inspectioneering</span>
            <span className="topbar-title-line2">
              {titleLine2} <span className="topbar-version">(v{APP_VERSION})</span>
            </span>
          </div>
        </div>

        {/* Breadcrumb — only shown when crumbs are set (object / inspection pages) */}
        {crumbs.length > 0 && (
          <div className="topbar-breadcrumb">
            {crumbs.map((crumb, i) => (
              <React.Fragment key={i}>
                {i > 0 && (
                  <svg className="topbar-breadcrumb-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                )}
                {crumb.path ? (
                  <button className="topbar-breadcrumb-link" onClick={() => navigate(crumb.path)}>
                    {crumb.label}
                  </button>
                ) : (
                  <span className="topbar-breadcrumb-current">{crumb.label}</span>
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        <div className="topbar-right">
          <ThemeToggle />
          {showPortalBack && (
            <button className="roles-back-btn" onClick={() => navigate('/')}>
              &larr; Back to Portal
            </button>
          )}
        </div>
      </header>

      {/* ── Body: Sidebar + Content ── */}
      <div className="layout-body">
        <nav className="sidebar">
          {isMergepoint ? (
            <>
              {/* Mergepoint shell: single dashboard + bridges to the others */}
              <div className="sidebar-section">
                <h3 className="sidebar-heading">MENU</h3>
                <NavLink to="/mergepoint" end className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`}>
                  Dashboard
                </NavLink>
              </div>
              <div className="sidebar-section">
                <h3 className="sidebar-heading">APPLICATION BRIDGES</h3>
                <NavLink to="/dashboard" className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`}>
                  TIRP
                </NavLink>
                <NavLink to="/terraflow" className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`}>
                  TerraFlow
                </NavLink>
              </div>
            </>
          ) : isTerraFlow ? (
            <>
              {/* TerraFlow Manager shell: slim menu + bridges */}
              <div className="sidebar-section">
                <h3 className="sidebar-heading">MENU</h3>
                <NavLink to="/terraflow" end className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`}>
                  Dashboard
                  <NotificationBadge count={pendingProjects} className="notif-badge-inline" title={`${pendingProjects} project(s) without a TIB object`} />
                  <NotificationBadge count={changedProjects} className="notif-badge-inline notif-badge-warning" title={`${changedProjects} project(s) updated by TerraFlow`} />
                </NavLink>
                <NavLink to="/customers" className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`}>
                  Customers
                  <NotificationBadge count={pendingCustomers} className="notif-badge-inline" />
                </NavLink>
              </div>
              <div className="sidebar-section">
                <h3 className="sidebar-heading">APPLICATION BRIDGES</h3>
                <NavLink to="/dashboard" className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`}>
                  TIRP
                </NavLink>
                <NavLink to="/mergepoint" className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`}>
                  Mergepoint
                </NavLink>
              </div>
            </>
          ) : (
            <>
              {/* Reporting Portal (TIRP) shell */}
              <div className="sidebar-section">
                <h3 className="sidebar-heading">MENU</h3>
                <NavLink to="/dashboard" end className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`}>
                  Dashboard
                </NavLink>
                <NavLink to="/test-sites" className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`}>
                  Test Sites
                </NavLink>
                <NavLink to="/objects" className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`}>
                  Objects
                </NavLink>
                <NavLink to="/inspections" className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`}>
                  Inspections
                </NavLink>
              </div>
              <div className="sidebar-section">
                <h3 className="sidebar-heading">DB MANAGEMENT</h3>
                <NavLink to="/equipment" className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`}>
                  Equipment
                </NavLink>
                <NavLink to="/ddl-collection" className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`}>
                  DDL Collection
                </NavLink>
                <NavLink to="/archived-projects" className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`}>
                  Archived Projects
                </NavLink>
              </div>
              <div className="sidebar-section">
                <h3 className="sidebar-heading">APPLICATION BRIDGES</h3>
                <NavLink to="/terraflow" className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`}>
                  TerraFlow
                </NavLink>
                <NavLink to="/mergepoint" className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`}>
                  Mergepoint
                </NavLink>
              </div>
            </>
          )}
          <div className="sidebar-section">
            <h3 className="sidebar-heading">HELP</h3>
            <NavLink to="/how-to-use" className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`}>
              How to Use
            </NavLink>
            <NavLink to="/support" className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`}>
              Support
            </NavLink>
          </div>

          {/* Sign out — pushed to the very bottom of the sidebar. The version
              now lives in the topbar title, so nothing sits above it here. */}
          <div className="sidebar-bottom">
            <button className="sidebar-signout" onClick={logout}>
              Sign out
            </button>
          </div>
        </nav>

        <main className="layout-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
